import React, { useCallback, useEffect, useRef, useState } from "react";
import { SegmentationMaskTypes } from "../pages/Playground/ImageToImage/Config/Features/EraseObject/eraseObjectConstants";
import useImageUtils from "../utils/useImageUtils";
import { useAppStore } from "./useStores";

interface SegmentationOverlayProps {
	mainCanvasRef: React.RefObject<HTMLCanvasElement>;
	panopticImageData: ImageData | null;
	maskImages: HTMLImageElement[];
	maskNumbers: number[];
	overlayOpacity?: number;
	onMaskClick?: (maskImage: HTMLImageElement) => void;
}

export const useSegmentationOverlay = ({
	mainCanvasRef,
	panopticImageData,
	maskImages,
	maskNumbers,
	overlayOpacity = 0.59,
	onMaskClick,
}: SegmentationOverlayProps) => {
	const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
	const [hoveredMaskIndex, setHoveredMaskIndex] = useState<number>(-1);
	const [isOverlayVisible, setIsOverlayVisible] = useState(false);
	const { imageToImageStore } = useAppStore();
	const { editingCanvasStore } = imageToImageStore;
	const { processWhitePixels } = useImageUtils();

	const isManualMask = editingCanvasStore.objectManipulationConfig.mask_type === SegmentationMaskTypes.MANUAL;

	const clearOverlay = useCallback(() => {
		const overlayCanvas = overlayCanvasRef.current;
		if (!overlayCanvas) return;

		const ctx = overlayCanvas.getContext("2d");
		if (!ctx) return;

		ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
	}, []);

	const drawMaskOverlay = useCallback(
		(maskIndex: number) => {
			if (isManualMask) return;

			const overlayCanvas = overlayCanvasRef.current;
			const mainCanvas = mainCanvasRef.current;
			if (!overlayCanvas || !mainCanvas || maskIndex < 0 || maskIndex >= maskImages.length) {
				clearOverlay();
				return;
			}

			const ctx = overlayCanvas.getContext("2d");
			if (!ctx) return;

			const maskImage = maskImages[maskIndex];
			if (!maskImage) return;

			clearOverlay();

			overlayCanvas.width = mainCanvas.width;
			overlayCanvas.height = mainCanvas.height;

			const scaleX = mainCanvas.width / maskImage.width;
			const scaleY = mainCanvas.height / maskImage.height;

			// Use utility to process the mask image with the desired overlay color
			const processedCanvas = processWhitePixels(maskImage, {
				r: 147,
				g: 51,
				b: 234,
				a: Math.floor(255 * overlayOpacity),
			});

			ctx.save();
			ctx.scale(scaleX, scaleY);
			ctx.drawImage(processedCanvas, 0, 0);
			ctx.restore();
		},
		[maskImages, overlayOpacity, clearOverlay, mainCanvasRef],
	);

	const getMaskIndexFromPosition = useCallback(
		(clientX: number, clientY: number): number => {
			if (!panopticImageData || !mainCanvasRef.current) {
				return -1;
			}

			const canvas = mainCanvasRef.current;
			const rect = canvas.getBoundingClientRect();

			const canvasX = ((clientX - rect.left) / rect.width) * canvas.width;
			const canvasY = ((clientY - rect.top) / rect.height) * canvas.height;

			const widthRatio = panopticImageData.width / canvas.width;
			const heightRatio = panopticImageData.height / canvas.height;

			const pixelX = Math.round(canvasX * widthRatio);
			const pixelY = Math.round(canvasY * heightRatio);

			if (pixelX < 0 || pixelX >= panopticImageData.width || pixelY < 0 || pixelY >= panopticImageData.height) {
				return -1;
			}

			const pixelIndex = (pixelY * panopticImageData.width + pixelX) * 4;
			const maskValue = panopticImageData.data[pixelIndex];

			const maskNumber = maskValue;
			const maskIndex = maskNumbers.findIndex((num) => num === maskNumber);

			return maskIndex >= 0 && maskIndex < maskImages.length ? maskIndex : -1;
		},
		[panopticImageData, maskImages.length, maskNumbers, mainCanvasRef],
	);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (isManualMask) return;
			const maskIndex = getMaskIndexFromPosition(e.clientX, e.clientY);

			if (maskIndex !== hoveredMaskIndex) {
				setHoveredMaskIndex(maskIndex);

				if (maskIndex >= 0) {
					setIsOverlayVisible(true);
					drawMaskOverlay(maskIndex);
				} else {
					setIsOverlayVisible(false);
					clearOverlay();
				}
			}
		},
		[getMaskIndexFromPosition, hoveredMaskIndex, drawMaskOverlay, clearOverlay],
	);

	const handleMouseLeave = useCallback(() => {
		if (isManualMask) return;

		setHoveredMaskIndex(-1);
		setIsOverlayVisible(false);
		clearOverlay();
	}, [getMaskIndexFromPosition, hoveredMaskIndex, drawMaskOverlay, clearOverlay]);

	// Handle click events
	const handleClick = useCallback(
		(e: MouseEvent) => {
			if (isManualMask) return;

			const maskIndex = getMaskIndexFromPosition(e.clientX, e.clientY);

			if (maskIndex >= 0 && maskIndex < maskImages.length && onMaskClick) {
				const maskImage = maskImages[maskIndex];
				onMaskClick(maskImage);
			}
		},
		[getMaskIndexFromPosition, maskImages, onMaskClick],
	);

	useEffect(() => {
		const mainCanvas = mainCanvasRef.current;
		if (!mainCanvas || isManualMask) return;

		mainCanvas.addEventListener("mousemove", handleMouseMove);
		mainCanvas.addEventListener("mouseleave", handleMouseLeave);
		mainCanvas.addEventListener("click", handleClick);

		return () => {
			mainCanvas.removeEventListener("mousemove", handleMouseMove);
			mainCanvas.removeEventListener("mouseleave", handleMouseLeave);
			mainCanvas.removeEventListener("click", handleClick);
		};
	}, [handleMouseMove, handleMouseLeave, handleClick]);

	return {
		overlayCanvasRef,
		hoveredMaskIndex,
		isOverlayVisible,
		clearOverlay,
		drawMaskOverlay,
	};
};
